import { ApolloError } from '@apollo/client';
import { Button, CircularProgress, Icon } from '@elipssolution/harfang';
import { mdiPencil, mdiPlus } from '@mdi/js';
import { Stack, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import ConfigurationForm from './ConfigurationForm';
import ExclusionsForm from './ExclusionsForm';
import FoldersTreeForm from './FoldersTreeForm';
import { useConfirmationDialogContext } from '../../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { generateErrorInformations } from '../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../utils/statusAnimationDelay';
import {
	CreateStorageDownloadType,
	FetchStorageDownloadType,
	RemoveStorageDownloadType,
	UpdateStorageDownloadType,
} from '../../api/settingStorageDownload';
import useDocumentConnector from '../../hooks/useDocumentConnector';
import useSettingStorageDownload from '../../hooks/useSettingStorageDownload';
import useSettingStorageDownloadMutations from '../../hooks/useSettingStorageDownloadMutations';
import { ConnectorType } from '../../types/connector';
import { SettingStorageDownloadType } from '../../types/settingStorage';
import { StorageDownloadFormType } from '../../types/storageDownloadFormType';

const ErrorWrapper = styled('div')(({ theme: { palette, shape, spacing } }) => ({
	height: 36,
	padding: spacing(0, 2),

	display: 'grid',
	placeItems: 'center',

	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	borderRadius: shape.borderRadius,
}));

const isMutationCreate = (
	data: CreateStorageDownloadType | UpdateStorageDownloadType | RemoveStorageDownloadType,
): data is CreateStorageDownloadType => 'document_createSettingStorageDownload' in data;
const isMutationRemove = (
	data: CreateStorageDownloadType | UpdateStorageDownloadType | RemoveStorageDownloadType,
): data is RemoveStorageDownloadType => 'document_removeSettingStorageDownload' in data;

enum FormStateEnum {
	IDLE,
	INITIAL_LOADING,
	INITIAL_LOAD_ERROR,
}

type StorageDownloadFormProps = {
	documentConnectorCode: ConnectorType['code'];
	storageDownloadId?: SettingStorageDownloadType['id'];
};

const StorageDownloadForm = ({ documentConnectorCode, storageDownloadId }: StorageDownloadFormProps) => {
	const { back, replace, onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const [succeededMutation, setSucceededMutation] = useState<string>();
	const [mutationErrorMessage, setMutationErrorMessage] = useState<string>();

	const {
		connector,
		loading: isFetchConnectorLoading,
		error: fetchConnectorError,
	} = useDocumentConnector({ variables: { code: documentConnectorCode } });
	const { name: connectorName, folders: connectorFolders = [], parameters: connectorParameters = [] } = connector ?? {};

	const onMutationCompleted = (
		data: CreateStorageDownloadType | UpdateStorageDownloadType | RemoveStorageDownloadType,
	) => {
		if (isMutationRemove(data)) {
			setSucceededMutation('remove');
			setTimeout(() => back(), SUCCESS_ANIMATION_DELAY);
		} else if (isMutationCreate(data)) {
			setSucceededMutation('create');
			setTimeout(
				() =>
					replace(
						<StorageDownloadForm
							documentConnectorCode={documentConnectorCode}
							storageDownloadId={data.document_createSettingStorageDownload.id}
						/>,
					),
				SUCCESS_ANIMATION_DELAY,
			);
		} else {
			setSucceededMutation('update');
			setTimeout(() => setSucceededMutation(undefined), SUCCESS_ANIMATION_DELAY);
		}
	};

	const onMutationError = ({ error, mutationName }: { error: ApolloError; mutationName: string }) => {
		setMutationErrorMessage(generateErrorInformations({ error, resource: `document_${mutationName}` }).message);

		throw error;
	};

	const {
		create,
		update,
		remove,
		loading: isMutationLoading,
	} = useSettingStorageDownloadMutations({
		onCompleted: onMutationCompleted,
		onError: onMutationError,
	});

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		reset,
	} = useForm<StorageDownloadFormType>({
		defaultValues: {
			exclusions: [{ value: '' }],
			folders: connectorFolders?.reduce((acc, { code }) => {
				acc[code] = '';

				return acc;
			}, {} as Record<string, string>),
		},
	});

	const applyStorageDownloadToForm = useCallback(
		({ document_settingStorageDownload }: FetchStorageDownloadType) => {
			const { configuration, exclusions, folders } = document_settingStorageDownload;

			reset({
				...(exclusions && {
					exclusions: (JSON.parse(exclusions ?? '[]') as string[]).map((exclusion) => ({
						value: exclusion,
					})),
				}),
				configuration: JSON.parse(configuration ?? '{}') as StorageDownloadFormType['configuration'],
				folders: JSON.parse(folders ?? '{}') as StorageDownloadFormType['folders'],
			});
		},
		[reset],
	);

	const { loading: isFetchSettingStorageDownloadLoading, error: fetchSettingStorageDownloadError } =
		useSettingStorageDownload({
			options: { onCompleted: applyStorageDownloadToForm, skip: !storageDownloadId },
			variables: { id: storageDownloadId ?? '' },
		});

	const loadErrorMessage = useMemo(() => {
		if (fetchConnectorError) {
			return generateErrorInformations({ error: fetchConnectorError, resource: 'document_connector' }).message;
		}

		if (fetchSettingStorageDownloadError) {
			return generateErrorInformations({
				error: fetchSettingStorageDownloadError,
				resource: 'document_settingStorageDownload',
			}).message;
		}

		return undefined;
	}, [fetchConnectorError, fetchSettingStorageDownloadError]);

	const formState = useMemo(() => {
		if (isFetchConnectorLoading || isFetchSettingStorageDownloadLoading) {
			return FormStateEnum.INITIAL_LOADING;
		}

		if (fetchConnectorError || fetchSettingStorageDownloadError) {
			return FormStateEnum.INITIAL_LOAD_ERROR;
		}

		return FormStateEnum.IDLE;
	}, [
		isFetchConnectorLoading,
		fetchConnectorError,
		isFetchSettingStorageDownloadLoading,
		fetchSettingStorageDownloadError,
	]);

	const isMutationButtonDisabled = useMemo(
		() =>
			[FormStateEnum.INITIAL_LOADING, FormStateEnum.INITIAL_LOAD_ERROR].includes(formState) ||
			isMutationLoading ||
			!isValid ||
			!isDirty,
		[formState, isDirty, isMutationLoading, isValid],
	);

	const mutationButtonLabel = useMemo(() => {
		if (storageDownloadId) {
			return succeededMutation === 'update' ? 'Configuration modifiée' : 'Modifier';
		}

		return succeededMutation === 'create' ? 'Configuration ajoutée' : 'Ajouter';
	}, [storageDownloadId, succeededMutation]);

	const onSubmit = useCallback(
		(values: StorageDownloadFormType) => {
			const { configuration, folders, exclusions } = values;

			const toSend = {
				connectorCode: documentConnectorCode,
				configuration: JSON.stringify(configuration),
				folders: JSON.stringify(folders),
				...(exclusions && {
					exclusions: JSON.stringify(exclusions.map(({ value }) => value)),
				}),
				...(storageDownloadId && {
					id: storageDownloadId,
				}),
			};

			const modeMutationInfoMap = new Map([
				['create', { mutation: create, mutationName: 'createSettingStorageDownload', inputValues: toSend }],
				[
					'update',
					{
						mutation: update,
						mutationName: 'updateSettingStorageDownload',
						inputValues: { ...toSend, id: storageDownloadId },
					},
				],
			]);

			const { mutation, mutationName = '' } = modeMutationInfoMap.get(storageDownloadId ? 'update' : 'create') ?? {};

			return mutation?.({
				variables: {
					[`${mutationName}Input`]: toSend,
				},
			}).then(() => {
				reset(values);
			});
		},
		[documentConnectorCode, create, reset, storageDownloadId, update],
	);

	const handleDeleteButtonClick = async () => {
		if (!connectorName) return undefined;

		const isDeleteConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer la configuration de consultation GED pour ${connectorName} ?`,
			content: `Les clients des dossiers rattachés à ${connectorName} ne pourront plus accéder à leur GED.`,
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isDeleteConfirmed) return null;

		return remove({ variables: { id: storageDownloadId } });
	};

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(
			`Êtes-vous sûr de vouloir quitter la ${connector ? 'modification' : 'création'} du connecteur ?`,
		);
	}, [connector, isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title={`GED${connectorName ? ` - ${connectorName}` : ''}`}>
			{formState !== FormStateEnum.INITIAL_LOADING && (
				<>
					<ConfigurationForm control={control} connectorParameters={connectorParameters} />
					{(connectorFolders?.length || 0) > 0 && (
						<FoldersTreeForm control={control} connectorFolders={connectorFolders} />
					)}
					<ExclusionsForm control={control} />

					<Stack direction="row-reverse" justifyContent="space-between">
						<Button
							disabled={isMutationButtonDisabled}
							onClick={handleSubmit(onSubmit)}
							startIcon={<Icon path={storageDownloadId ? mdiPencil : mdiPlus} />}
							variant="contained"
						>
							{mutationButtonLabel}
						</Button>

						{(loadErrorMessage || mutationErrorMessage) && (
							<ErrorWrapper>{loadErrorMessage || mutationErrorMessage}</ErrorWrapper>
						)}

						{storageDownloadId && (
							<Button color="error" onClick={handleDeleteButtonClick} variant="outlined">
								{succeededMutation === 'remove' ? 'Configuration supprimée' : 'Supprimer'}
							</Button>
						)}
					</Stack>
				</>
			)}
			{formState === FormStateEnum.INITIAL_LOADING && (
				<Stack alignItems="center" paddingTop={2} gap={2}>
					<CircularProgress color="inherit" />
					Chargement des informations du connecteur et de la consultation GED
				</Stack>
			)}
		</SettingsDialogPage>
	);
};

export default StorageDownloadForm;
